import React from 'react';
import * as Sentry from '@sentry/react';
import { TriangleAlert, RefreshCw } from 'lucide-react';

interface ErrorFallbackProps {
    error: Error;
    resetError: () => void;
    eventId: string | null;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, resetError, eventId }) => {
    const isDevelopment = import.meta.env.MODE === 'development';

    const handleReportFeedback = () => {
        if (eventId) {
            Sentry.showReportDialog({ eventId });
        }
    };

    const handleGoHome = () => {
        window.location.href = '/dashboard';
    };

    return (
        <div className="min-h-screen bg-snapfy-bg flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                    <div className="text-center">
                        <TriangleAlert className="mx-auto h-12 w-12 text-red-500" />
                        <h2 className="mt-6 text-3xl font-extrabold text-snapfy-text-primary">
                            Ops! Algo deu errado.
                        </h2>
                        <p className="mt-2 text-sm text-snapfy-text-secondary">
                            Desculpe, ocorreu um erro inesperado. Nossa equipe foi notificada.
                        </p>
                    </div>

                    {isDevelopment && (
                        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
                            <h3 className="text-sm font-medium text-red-800 mb-2">
                                Detalhes do erro (Desenvolvimento)
                            </h3>
                            <p className="text-sm text-red-700 font-mono break-all">
                                {error.message}
                            </p>
                            {error.stack && (
                                <details className="mt-2">
                                    <summary className="text-sm text-red-800 cursor-pointer">
                                        Stack Trace
                                    </summary>
                                    <pre className="mt-2 text-xs text-red-700 whitespace-pre-wrap">
                                        {error.stack}
                                    </pre>
                                </details>
                            )}
                        </div>
                    )}

                    <div className="mt-6 space-y-3">
                        <button
                            onClick={resetError}
                            className="w-full flex justify-center items-center gap-2 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#7063f7] hover:bg-[#5a4dd4] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7063f7] transition-colors"
                        >
                            <RefreshCw className="w-4 h-4" />
                            Tentar novamente
                        </button>

                        <button
                            onClick={handleGoHome}
                            className="w-full flex justify-center items-center gap-2 px-4 py-2 border border-snapfy-border rounded-md shadow-sm text-sm font-medium text-snapfy-text-secondary bg-white hover:bg-snapfy-bg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7063f7] transition-colors"
                        >
                            Voltar ao início
                        </button>

                        {eventId && (
                            <button
                                onClick={handleReportFeedback}
                                className="w-full flex justify-center items-center gap-2 px-4 py-2 border border-snapfy-border rounded-md shadow-sm text-sm font-medium text-snapfy-text-secondary bg-white hover:bg-snapfy-bg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7063f7] transition-colors"
                            >
                                <TriangleAlert className="w-4 h-4" />
                                Reportar problema
                            </button>
                        )}
                    </div>

                    {eventId && (
                        <div className="mt-4 text-center">
                            <p className="text-xs text-snapfy-text-secondary">
                                ID do Erro: <code className="bg-snapfy-inner px-1 rounded">{eventId}</code>
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Create the Sentry Error Boundary with custom fallback
const SentryErrorBoundary = Sentry.withErrorBoundary(
    ({ children }: { children: React.ReactNode }) => <>{children}</>,
    {
        fallback: ErrorFallback,
        beforeCapture: (scope, error, errorInfo) => {
            // Add additional context to the error
            scope.setTag('errorBoundary', 'SentryErrorBoundary');
            scope.setContext('errorInfo', {
                componentStack: errorInfo.componentStack,
                errorBoundary: 'SentryErrorBoundary',
            });

            // Add breadcrumb for the error boundary capture
            Sentry.addBreadcrumb({
                message: 'Error caught by SentryErrorBoundary',
                category: 'error',
                level: 'error',
                data: {
                    error: error.message,
                    componentStack: errorInfo.componentStack,
                },
            });
        },
    }
);

export default SentryErrorBoundary;
